import { type CompanyDto, type ContractInvoicingDto, datePl, formatMoney, INVOICE_KINDS, type InvoicingOverviewDto, type MeDto, todayInWarsaw } from '@bqcrm/shared';
import { Alert, Anchor, Badge, Button, Group, Stack, Table, Text } from '@mantine/core';
import { IconCash, IconCircleCheck, IconFileDollar, IconFileInvoice, IconPlugConnectedX, IconReceipt } from '@tabler/icons-react';
import { useQueryClient, type UseQueryResult } from '@tanstack/react-query';
import { useEffect, useRef, useState } from 'react';
import { Link, useSearchParams } from 'react-router';
import { api, errorText } from '../api';
import { can } from '../auth';
import { CompanyBadgeById, InvoicePayStatusBadge, TestModeBadge } from '../components/Badges';
import { InvoicePaymentModal, PendingInvoicePayments } from '../components/InvoicePayment';
import { AnnexInvoiceModal, confirmDocument, FinalInvoiceModal } from '../components/InvoicesPanel';
import { Money } from '../components/Money';
import { QueryState } from '../components/QueryState';
import { EmptyState, SectionCard } from '../components/ui';
import { notifyError, notifyOk } from '../notify';

/**
 * Wystawianie dokumentów prosto z Finansów — te same endpointy co zakładka „Faktury” umowy, więc dokument powstaje
 * w Fakturowni (przez integrację). Przed potwierdzeniem pobieramy stan faktur umowy (wysyłka, tryb konta).
 */
function useFinanceIssue() {
  const qc = useQueryClient();
  const [busy, setBusy] = useState<string | null>(null);
  const [finalFor, setFinalFor] = useState<{ contractId: string; st: ContractInvoicingDto; annexId?: string | null } | null>(null);
  const refresh = () => {
    void qc.invalidateQueries({ queryKey: ['invoicing-overview'] });
    void qc.invalidateQueries({ queryKey: ['receivables'] });
  };
  const load = (contractId: string) => qc.fetchQuery({ queryKey: ['contract-invoicing', contractId], queryFn: () => api<ContractInvoicingDto>(`/contracts/${contractId}/invoicing`), staleTime: 0 });
  // błąd z podpowiedzią, gdzie ponowić wystawienie
  const fail = (e: unknown) => notifyError(new Error(`Nie wystawiono dokumentu: ${errorText(e)}\nSzczegóły i ponowienie — na umowie, w zakładce „Faktury”.`));

  /** Potwierdzenie (kwota, wysyłka, KSeF) → wystawienie w Fakturowni → odświeżenie przeglądu. */
  const issue = async (o: { key: string; contractId: string; title: string; lines: string[]; fiscal?: boolean; path: string; body: unknown; ok: string }) => {
    setBusy(o.key);
    let sent = false;
    try {
      const st = await load(o.contractId);
      // QA P4: pro forma do pozycji po terminie przedłuża termin — ostrzeżenie w potwierdzeniu
      const late = o.path === 'proforma' ? st.schedule.find((x) => x.paymentItemId === (o.body as { paymentItemId?: string }).paymentItemId)?.proformaWarning : null;
      if (!(await confirmDocument(st, o.title, late ? [...o.lines, late] : o.lines, o.fiscal ?? true))) return;
      sent = true;
      const s = await api<ContractInvoicingDto>(`/contracts/${o.contractId}/invoices/${o.path}`, { method: 'POST', body: o.body });
      qc.setQueryData(['contract-invoicing', o.contractId], s);
      notifyOk(o.ok);
    } catch (e) {
      fail(e);
    } finally {
      setBusy(null);
      if (sent) refresh();
    }
  };

  const openFinal = async (contractId: string, annexId?: string | null) => {
    setBusy(`final:${contractId}`);
    try {
      setFinalFor({ contractId, st: await load(contractId), annexId });
    } catch (e) {
      fail(e);
    } finally {
      setBusy(null);
    }
  };

  // P1: faktura VAT do aneksu — osobne okno (pozycje aneksu)
  const modal =
    finalFor &&
    (finalFor.annexId ? (
      <AnnexInvoiceModal st={finalFor.st} contractId={finalFor.contractId} annexId={finalFor.annexId} opened onClose={() => setFinalFor(null)} onIssued={refresh} />
    ) : (
      <FinalInvoiceModal st={finalFor.st} contractId={finalFor.contractId} opened onClose={() => setFinalFor(null)} onIssued={refresh} />
    ));
  return { busy, issue, openFinal, modal };
}
type Issue = ReturnType<typeof useFinanceIssue>;

/** Liczba dni między datami RRRR-MM-DD (b − a), bez wpływu zmiany czasu. */
const daysBetween = (a: string, b: string) => Math.round((Date.parse(`${b}T12:00:00Z`) - Date.parse(`${a}T12:00:00Z`)) / 86_400_000);
const days = (n: number) => `${n} ${n === 1 ? 'dzień' : 'dni'}`;
const LATE_BG = { backgroundColor: 'var(--mantine-color-red-light)' };

/** Umowa (link do zakładki faktur) i sprawa z klientem. */
function ContractCell({ r }: { r: { contractId: string; contractNumber: string | null; caseId: string; caseNumber: string; clientName: string; companyId: string } }) {
  return (
    <>
      <Group gap={6} wrap="nowrap">
        <CompanyBadgeById id={r.companyId} />
        <Anchor component={Link} to={`/umowy/${r.contractId}?zakladka=faktury`} size="sm">
          {r.contractNumber ?? 'umowa'}
        </Anchor>
      </Group>
      <Text size="xs" c="dimmed">
        <Anchor component={Link} to={`/sprawy/${r.caseId}?zakladka=platnosci`} size="xs">
          {r.caseNumber}
        </Anchor>{' '}
        · {r.clientName}
      </Text>
    </>
  );
}

/**
 * Finanse → Fakturowanie: wpłaty bez faktury (termin: 15. dzień następnego miesiąca) i nieopłacone faktury końcowe / VAT.
 * Wystawianie wprost z listy (zaliczkowa / pro forma / końcowa) — przez integrację z Fakturownią, jak na umowie (zakładka „Faktury”).
 */
export function FinanceInvoicing({ me, q, companies, companyId }: { me: MeDto; q: UseQueryResult<InvoicingOverviewDto>; companies: CompanyDto[]; companyId: string | null }) {
  const today = todayInWarsaw();
  const d = q.data;
  const issue = useFinanceIssue();
  const canIssue = !!d?.canIssue;
  // QA qa10311123 3.7: ?aneks=numer — z zadania „Wystaw fakturę VAT do aneksu …”: od razu okno faktury do aneksu
  const [searchParams, setSearchParams] = useSearchParams();
  const annexAsked = searchParams.get('aneks');
  const annexOpened = useRef<string | null>(null);
  useEffect(() => {
    if (!annexAsked || !d || annexOpened.current === annexAsked) return;
    annexOpened.current = annexAsked;
    const row = d.finalDue.find((r) => r.annexContractId && r.annexNumber === annexAsked && !r.blockReason);
    if (row && canIssue) void issue.openFinal(row.contractId, row.annexContractId);
    setSearchParams(
      (prev) => {
        const n = new URLSearchParams(prev);
        n.delete('aneks');
        return n;
      },
      { replace: true },
    );
  }, [annexAsked, d, canIssue, issue, setSearchParams]);
  const inCompany = <T extends { companyId: string }>(rows: T[]) => (companyId ? rows.filter((r) => r.companyId === companyId) : rows);
  const shown = (d?.companies ?? []).filter((c) => !companyId || c.companyId === companyId);
  const disconnected = shown.filter((c) => !c.connected);
  const name = (id: string) => companies.find((c) => c.id === id)?.shortName ?? 'firma';
  const settingsLink = can(me, 'integracje.konfiguracja', 'W') ? (
    <Anchor component={Link} to="/ustawienia/firmy" size="sm">
      Ustawienia → Firmy
    </Anchor>
  ) : (
    'Ustawienia → Firmy (właściciel / administrator)'
  );

  return (
    <QueryState isLoading={q.isLoading} error={q.error}>
      {d && (
        <Stack gap="md">
          {shown.length > 0 && disconnected.length === shown.length ? (
            <SectionCard>
              <EmptyState
                icon={<IconPlugConnectedX size={24} />}
                title={shown.length === 1 ? `${name(shown[0]!.companyId)} nie ma podłączonej Fakturowni` : 'Żadna firma nie ma podłączonej Fakturowni'}
                hint={<>Bez integracji CRM nie wie, które wpłaty zafakturowano — faktury wystawiaj w systemie księgowym. Integrację włączysz w: {settingsLink}.</>}
              />
            </SectionCard>
          ) : (
            <>
              {disconnected.length > 0 && (
                <Alert color="gray" icon={<IconPlugConnectedX size={18} />}>
                  Bez podłączonej Fakturowni: <b>{disconnected.map((c) => name(c.companyId)).join(', ')}</b> — wpłat tych firm nie ma na liście „Wpłaty bez faktury” (faktury wystawiane są poza CRM). Integracja: {settingsLink}.
                </Alert>
              )}
              <UninvoicedPayments rows={inCompany(d.uninvoicedPayments)} today={today} issue={canIssue ? issue : null} />
              <ProformaDue rows={inCompany(d.proformaDue)} issue={canIssue ? issue : null} />
              <FinalDue rows={inCompany(d.finalDue)} issue={canIssue ? issue : null} />
              <UnpaidInvoices rows={inCompany(d.unpaidInvoices)} today={today} canSync={canIssue} />
            </>
          )}
        </Stack>
      )}
      {issue.modal}
    </QueryState>
  );
}

/** Przycisk wystawienia w wierszu przeglądu; przy blokadzie — powód zamiast przycisku. */
function IssueCell({ issue, busyKey, blockReason, label, onClick }: { issue: Issue; busyKey: string; blockReason: string; label: string; onClick: () => void }) {
  if (blockReason)
    return (
      <Text size="xs" c="orange.8" maw={220}>
        {blockReason}
      </Text>
    );
  return (
    <Button size="compact-xs" variant="light" leftSection={<IconFileInvoice size={14} />} loading={issue.busy === busyKey} disabled={!!issue.busy && issue.busy !== busyKey} onClick={onClick}>
      {label}
    </Button>
  );
}

function UninvoicedPayments({ rows, today, issue }: { rows: InvoicingOverviewDto['uninvoicedPayments']; today: string; issue: Issue | null }) {
  const late = rows.filter((r) => r.overdue).length;
  return (
    <SectionCard
      title={`Wpłaty bez faktury (${rows.length})`}
      icon={<IconFileInvoice size={16} />}
      actions={
        late > 0 ? (
          <Badge color="red" variant="light">
            po terminie: {late}
          </Badge>
        ) : undefined
      }
      padding={0}
    >
      <Text size="xs" c="dimmed" px="md" pb="xs">
        Fakturę zaliczkową do otrzymanej wpłaty wystawia się najpóźniej 15. dnia następnego miesiąca. Wpłaty na płatność końcową rozlicza faktura końcowa — nie ma ich tutaj.
      </Text>
      {rows.length ? (
        <Table.ScrollContainer minWidth={760}>
          <Table highlightOnHover>
            <Table.Thead>
              <Table.Tr>
                <Table.Th>Umowa / sprawa</Table.Th>
                <Table.Th>Płatność</Table.Th>
                <Table.Th ta="right">Kwota</Table.Th>
                <Table.Th>Data wpłaty</Table.Th>
                <Table.Th>Wystawić do</Table.Th>
                {issue && <Table.Th />}
              </Table.Tr>
            </Table.Thead>
            <Table.Tbody>
              {rows.map((r) => {
                const left = daysBetween(today, r.deadline);
                return (
                  <Table.Tr key={r.paymentId} style={r.overdue ? LATE_BG : undefined}>
                    <Table.Td>
                      <ContractCell r={r} />
                    </Table.Td>
                    <Table.Td>
                      <Text size="sm">{r.itemName}</Text>
                      {r.blockReason && !issue && (
                        <Text size="xs" c="orange.8">
                          {r.blockReason}
                        </Text>
                      )}
                    </Table.Td>
                    <Table.Td ta="right">
                      <Money gr={r.amountGr} />
                    </Table.Td>
                    <Table.Td>{datePl(r.paidAt)}</Table.Td>
                    <Table.Td>
                      <Text size="sm" c={r.overdue ? 'var(--app-text-red)' : undefined} fw={r.overdue ? 600 : 400}>
                        {datePl(r.deadline)}
                      </Text>
                      <Text size="xs" c={r.overdue ? 'var(--app-text-red)' : left <= 5 ? 'orange.8' : 'dimmed'}>
                        {r.overdue ? `po terminie ${days(-left)}` : left === 0 ? 'termin dziś' : `zostało ${days(left)}`}
                      </Text>
                    </Table.Td>
                    {issue && (
                      <Table.Td ta="right">
                        <IssueCell
                          issue={issue}
                          busyKey={`adv:${r.paymentId}`}
                          blockReason={r.blockReason}
                          label="Wystaw fakturę zaliczkową"
                          onClick={() =>
                            void issue.issue({
                              key: `adv:${r.paymentId}`,
                              contractId: r.contractId,
                              title: 'Wystawić fakturę zaliczkową?',
                              lines: [`Faktura zaliczkowa na wpłatę ${formatMoney(r.amountGr)} z ${datePl(r.paidAt)} (${r.itemName}) — umowa ${r.contractNumber ?? ''}, ${r.clientName}. Oznaczona jako opłacona.`],
                              path: 'advance',
                              body: { paymentId: r.paymentId },
                              ok: 'Wystawiono fakturę zaliczkową',
                            })
                          }
                        />
                      </Table.Td>
                    )}
                  </Table.Tr>
                );
              })}
            </Table.Tbody>
          </Table>
        </Table.ScrollContainer>
      ) : (
        <EmptyState compact icon={<IconCircleCheck size={18} />} title="Wszystkie wpłaty mają faktury" />
      )}
    </SectionCard>
  );
}

function ProformaDue({ rows, issue }: { rows: InvoicingOverviewDto['proformaDue']; issue: Issue | null }) {
  return (
    <SectionCard title={`Pro formy do wystawienia (${rows.length})`} icon={<IconReceipt size={16} />} padding={0}>
      <Text size="xs" c="dimmed" px="md" pb="xs">
        Pozycje harmonogramu z wymaganą pro formą, których zdarzenie już zaszło (np. podpis umowy). Pro forma to wezwanie do zapłaty — bez skutków podatkowych, poza KSeF.
      </Text>
      {rows.length ? (
        <Table.ScrollContainer minWidth={760}>
          <Table highlightOnHover>
            <Table.Thead>
              <Table.Tr>
                <Table.Th>Umowa / sprawa</Table.Th>
                <Table.Th>Płatność</Table.Th>
                <Table.Th ta="right">Do zapłaty</Table.Th>
                <Table.Th>Termin</Table.Th>
                {issue && <Table.Th />}
              </Table.Tr>
            </Table.Thead>
            <Table.Tbody>
              {rows.map((r) => (
                <Table.Tr key={r.paymentItemId}>
                  <Table.Td>
                    <ContractCell r={r} />
                  </Table.Td>
                  <Table.Td>
                    <Text size="sm">{r.itemName}</Text>
                    {r.blockReason && !issue && (
                      <Text size="xs" c="orange.8">
                        {r.blockReason}
                      </Text>
                    )}
                  </Table.Td>
                  <Table.Td ta="right">
                    <Money gr={r.amountGr - r.paidGr} />
                  </Table.Td>
                  <Table.Td>{r.dueDate ? datePl(r.dueDate) : '—'}</Table.Td>
                  {issue && (
                    <Table.Td ta="right">
                      <IssueCell
                        issue={issue}
                        busyKey={`pf:${r.paymentItemId}`}
                        blockReason={r.blockReason}
                        label="Wystaw pro formę"
                        onClick={() =>
                          void issue.issue({
                            key: `pf:${r.paymentItemId}`,
                            contractId: r.contractId,
                            title: 'Wystawić pro formę?',
                            lines: [`Pro forma na ${formatMoney(r.amountGr - r.paidGr)} (${r.itemName}) — umowa ${r.contractNumber ?? ''}, ${r.clientName}.`, 'To wezwanie do zapłaty — bez skutków podatkowych, nie trafia do KSeF.'],
                            fiscal: false,
                            path: 'proforma',
                            body: { paymentItemId: r.paymentItemId },
                            ok: 'Wystawiono pro formę',
                          })
                        }
                      />
                    </Table.Td>
                  )}
                </Table.Tr>
              ))}
            </Table.Tbody>
          </Table>
        </Table.ScrollContainer>
      ) : (
        <EmptyState compact icon={<IconCircleCheck size={18} />} title="Brak pro form do wystawienia" />
      )}
    </SectionCard>
  );
}

function FinalDue({ rows, issue }: { rows: InvoicingOverviewDto['finalDue']; issue: Issue | null }) {
  return (
    <SectionCard title={`Faktury końcowe do wystawienia (${rows.length})`} icon={<IconFileDollar size={16} />} padding={0}>
      <Text size="xs" c="dimmed" px="md" pb="xs">
        Umowy, w których płatność końcowa jest należna (np. po odbiorze) albo już wpłacona, a faktury końcowej / VAT jeszcze nie ma. Faktura końcowa rozlicza wystawione faktury zaliczkowe.
      </Text>
      {rows.length ? (
        <Table.ScrollContainer minWidth={760}>
          <Table highlightOnHover>
            <Table.Thead>
              <Table.Tr>
                <Table.Th>Umowa / sprawa</Table.Th>
                <Table.Th>Dokument</Table.Th>
                <Table.Th ta="right">Wartość umowy</Table.Th>
                <Table.Th ta="right">Zaliczki</Table.Th>
                <Table.Th ta="right">Wpłacono na końcową</Table.Th>
                {issue && <Table.Th />}
              </Table.Tr>
            </Table.Thead>
            <Table.Tbody>
              {rows.map((r) => (
                <Table.Tr key={r.annexContractId ?? r.contractId}>
                  <Table.Td>
                    <ContractCell r={r} />
                  </Table.Td>
                  <Table.Td>
                    <Text size="sm">{r.annexContractId ? `Faktura VAT do aneksu ${r.annexNumber ?? ''}` : r.kind === 'koncowa' ? 'Faktura końcowa' : 'Faktura VAT'}</Text>
                    {r.itemName && (
                      <Text size="xs" c="dimmed">
                        {r.itemName}
                      </Text>
                    )}
                    {r.blockReason && !issue && (
                      <Text size="xs" c="orange.8">
                        {r.blockReason}
                      </Text>
                    )}
                  </Table.Td>
                  <Table.Td ta="right">
                    <Money gr={r.contractGrossGr} />
                  </Table.Td>
                  <Table.Td ta="right">
                    <Money gr={r.advancesGr} />
                  </Table.Td>
                  <Table.Td ta="right">
                    <Money gr={r.alreadyPaidGr} />
                  </Table.Td>
                  {issue && (
                    <Table.Td ta="right">
                      <IssueCell
                        issue={issue}
                        busyKey={`final:${r.contractId}`}
                        blockReason={r.blockReason}
                        label={r.kind === 'koncowa' ? 'Wystaw fakturę końcową' : 'Wystaw fakturę VAT'}
                        onClick={() => void issue.openFinal(r.contractId, r.annexContractId)}
                      />
                    </Table.Td>
                  )}
                </Table.Tr>
              ))}
            </Table.Tbody>
          </Table>
        </Table.ScrollContainer>
      ) : (
        <EmptyState compact icon={<IconCircleCheck size={18} />} title="Brak faktur końcowych do wystawienia" />
      )}
    </SectionCard>
  );
}

function UnpaidInvoices({ rows, today, canSync }: { rows: InvoicingOverviewDto['unpaidInvoices']; today: string; canSync: boolean }) {
  const late = rows.filter((r) => r.overdue).length;
  const [paying, setPaying] = useState<InvoicingOverviewDto['unpaidInvoices'][number] | null>(null);
  const withActions = rows.some((r) => r.canRegisterPayment);
  return (
    <SectionCard
      title={`Nieopłacone faktury końcowe i VAT (${rows.length})`}
      icon={<IconFileInvoice size={16} />}
      actions={
        late > 0 ? (
          <Badge color="red" variant="light">
            po terminie płatności: {late}
          </Badge>
        ) : undefined
      }
      padding={0}
    >
      <Text size="xs" c="dimmed" px="md" pb="xs">
        Stan opłacenia z Fakturowni (odświeżany automatycznie). Opłacenie oznaczone w Fakturowni staje się wpłatą w CRM; wpłata zarejestrowana tutaj trafia do Fakturowni i zmienia status faktury.
      </Text>
      {rows.length ? (
        <Table.ScrollContainer minWidth={820}>
          <Table highlightOnHover>
            <Table.Thead>
              <Table.Tr>
                <Table.Th>Umowa / sprawa</Table.Th>
                <Table.Th>Faktura</Table.Th>
                <Table.Th ta="right">Brutto</Table.Th>
                <Table.Th ta="right">Opłacono</Table.Th>
                <Table.Th>Termin płatności</Table.Th>
                <Table.Th>Stan</Table.Th>
                {withActions && <Table.Th />}
              </Table.Tr>
            </Table.Thead>
            <Table.Tbody>
              {rows.map((r) => (
                <Table.Tr key={r.invoiceId} style={r.overdue ? LATE_BG : undefined}>
                  <Table.Td>
                    <ContractCell r={r} />
                  </Table.Td>
                  <Table.Td>
                    <Group gap={6} wrap="nowrap">
                      <Text size="sm" fw={600}>
                        {r.number ?? '—'}
                      </Text>
                      {r.mode === 'test' && <TestModeBadge />}
                    </Group>
                    <Text size="xs" c="dimmed">
                      {INVOICE_KINDS[r.kind]}
                      {r.issueDate ? ` · wystawiona ${datePl(r.issueDate)}` : ''}
                    </Text>
                  </Table.Td>
                  <Table.Td ta="right">
                    <Money gr={r.grossGr} />
                  </Table.Td>
                  <Table.Td ta="right">
                    <Money gr={r.paidGr} />
                  </Table.Td>
                  <Table.Td>
                    <Text size="sm" c={r.overdue ? 'var(--app-text-red)' : undefined} fw={r.overdue ? 600 : 400}>
                      {r.paymentTo ? datePl(r.paymentTo) : '—'}
                    </Text>
                    {r.overdue && r.paymentTo && (
                      <Text size="xs" c="var(--app-text-red)">
                        po terminie {days(daysBetween(r.paymentTo, today))}
                      </Text>
                    )}
                  </Table.Td>
                  <Table.Td>
                    <InvoicePayStatusBadge extStatus={r.extStatus} overdue={r.overdue} />
                    <PendingInvoicePayments contractId={r.contractId} pending={r.pendingPayments} canRetry={canSync} />
                  </Table.Td>
                  {withActions && (
                    <Table.Td ta="right">
                      {r.canRegisterPayment && (
                        <Button size="compact-xs" variant="light" color="green" leftSection={<IconCash size={14} />} onClick={() => setPaying(r)}>
                          Zarejestruj wpłatę
                        </Button>
                      )}
                    </Table.Td>
                  )}
                </Table.Tr>
              ))}
            </Table.Tbody>
          </Table>
        </Table.ScrollContainer>
      ) : (
        <EmptyState compact icon={<IconCircleCheck size={18} />} title="Brak nieopłaconych faktur końcowych i VAT" />
      )}
      {paying && <InvoicePaymentModal invoiceId={paying.invoiceId} number={paying.number} contractId={paying.contractId} toPayGr={paying.toPayGr} opened onClose={() => setPaying(null)} />}
    </SectionCard>
  );
}
